/** 書類の取得手段。自社取得済み登記／リユース登記／新規取得 */
export type AcquireStep = "acquired" | "reuse" | "new";

/**
 * 画面側の取得条件。候補の選抜・優先順・時間窓はすべて画面が決め、
 * BE へ送るのは「候補のない書類を新規取得に回すか」だけなので、送信の形とは分けて持つ
 */
export interface AcquirePolicy {
  /** 使う手段を探索順に並べたもの。新規取得は候補が常に存在する終点なので、含まれる場合は末尾 */
  steps: AcquireStep[];
  /** 自社取得済み・リユースの時間窓（経過日数）を使わず、取得日を問わず候補にする */
  ignoreWindow: boolean;
}

/** 取得方法セレクトの選択肢。画面内だけの概念で、BE へは送らない */
export type AcquireMethod =
  | "NEW_ONLY"
  | "REUSE_PRIORITY"
  | "REUSE_ONLY"
  | "ACQUIRED_PRIORITY"
  | "ACQUIRED_ONLY";

/** 取得方法を指定する方式の5方式。経過日数は使わず、選択肢の名前どおりの手段だけを使う */
export const POLICY_PRESETS: Record<AcquireMethod, AcquirePolicy> = {
  NEW_ONLY: { steps: ["new"], ignoreWindow: true },
  REUSE_PRIORITY: { steps: ["reuse", "new"], ignoreWindow: true },
  REUSE_ONLY: { steps: ["reuse"], ignoreWindow: true },
  ACQUIRED_PRIORITY: {
    steps: ["acquired", "reuse", "new"],
    ignoreWindow: true,
  },
  ACQUIRED_ONLY: { steps: ["acquired"], ignoreWindow: true },
};

/**
 * 経過日数を指定する方式の固定条件。取得方法は選ばせず、
 * 自社取得済みを先に拾ってから（費用のかからない候補を取り逃さない）リユース・新規へ進む
 */
export const LEGACY_POLICY: AcquirePolicy = {
  steps: ["acquired", "reuse", "new"],
  ignoreWindow: false,
};

const samePolicy = (a: AcquirePolicy, b: AcquirePolicy): boolean =>
  a.ignoreWindow === b.ignoreWindow &&
  a.steps.length === b.steps.length &&
  a.steps.every((step, index) => step === b.steps[index]);

/**
 * 条件が取得方法のどれかと一致すればその方式、しなければ null（＝カスタム）
 *
 * @example
 * matchPreset({ steps: ["reuse"], ignoreWindow: true }) // "REUSE_ONLY"
 * matchPreset({ steps: ["reuse", "acquired"], ignoreWindow: true }) // null
 */
export const matchPreset = (policy: AcquirePolicy): AcquireMethod | null =>
  (Object.keys(POLICY_PRESETS) as AcquireMethod[]).find((method) =>
    samePolicy(POLICY_PRESETS[method], policy)
  ) ?? null;

export const usesNew = (policy: AcquirePolicy): boolean =>
  policy.steps.includes("new");

const STEP_LABEL: Record<AcquireStep, string> = {
  acquired: "自社取得済み",
  reuse: "リユース",
  new: "新規",
};

/** 段ごとの料金区分。要約と詳細のチェックボックスで同じ語を使う（自社取得済みには付けない） */
export const STEP_COST: Partial<Record<AcquireStep, string>> = {
  reuse: "安価",
  new: "通常料金",
};

/**
 * カスタム条件の優先順位の要約
 *
 * @example
 * summarizePolicy({ steps: ["acquired", "reuse", "new"], ignoreWindow: true })
 * // "自社取得済み → リユース（安価） → 新規（通常料金）"
 * summarizePolicy({ steps: [], ignoreWindow: true }) // "取得しない"
 */
export const summarizePolicy = (policy: AcquirePolicy): string => {
  const order = policy.steps
    .map((step) => {
      const cost = STEP_COST[step];
      return cost == null ? STEP_LABEL[step] : `${STEP_LABEL[step]}（${cost}）`;
    })
    .join(" → ");
  return order === "" ? "取得しない" : order;
};
